/**
 * Профиль/настройки (auth-and-roles «Профиль пользователя»): почта, смена пароля.
 * Смена почты в Блоке 1 не имеет эндпоинта — поле read-only с пометкой (см. отчёт по блоку).
 * Выход из системы перенесён в шапку (centrika-docs!58) — здесь его больше нет.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '@/app/auth-context'
import {
  authApi,
  companyApi,
  ecologyClientApi,
  type CompanyProfile,
  type UpdateCompanyProfileInput,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { Hint } from '@/design-system/components/primitives/text'
import { Badge } from '@/design-system/components/primitives/badge'
import { SettingRow } from '@/design-system/components/primitives/setting-row'
import { Switch } from '@/design-system/components/primitives/switch'
import { CompanyProfileForm } from '@/company/company-profile-form'
import { ContactsManager } from '@/company/contacts-manager'
import { EcoRequisites } from '@/ecology/eco-requisites'
import {
  NOTIFICATION_INFO_KINDS,
  NOTIFICATION_INFO_KINDS_COMPANY,
  NOTIFICATION_KIND_LABELS,
} from '@/lib/notifications'

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setDone(false)
    setSubmitting(true)
    try {
      await authApi.changePassword({ currentPassword, newPassword })
      setDone(true)
      setCurrentPassword('')
      setNewPassword('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось изменить пароль'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card header="Смена пароля">
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        {done ? (
          <Alert variant="info" className="mb-4">
            Пароль изменён. Другие сессии завершены.
          </Alert>
        ) : null}
        <Field required label="Текущий пароль">
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Field required label="Новый пароль">
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Сохранение…' : 'Изменить пароль'}
        </Button>
      </form>
    </Card>
  )
}

/**
 * Блок «Организация» в Настройках клиента-компании (company-profile-and-contacts).
 * Фаза 2 (design D1, _РЕШЕНИЯ_PM_фаза2.md A1): реквизиты правит клиент сам, без
 * модерации — форма вместо прежней read-only карточки; менеджерские поля («профиль
 * отрасли», «есть ОПО») скрыты (`hideManagerFields`). Раздел доступен всегда — это и
 * есть «вернуться к заполнению профиля» из §3.1 (D5): отдельного действия не требуется,
 * форма уже открыта. При неполном профиле — подсказка сверху.
 */
function CompanyOrgSection({ currentUserEmail }: { currentUserEmail: string }) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    companyApi
      .getProfile(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setProfile(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить реквизиты'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  async function handleSave(input: UpdateCompanyProfileInput): Promise<CompanyProfile> {
    const updated = await companyApi.updateProfile(input)
    setProfile(updated)
    return updated
  }

  return (
    <Grid cols={2} className="mt-4">
      {loading ? (
        <Card header="Организация">
          <p className="py-4 text-txt-secondary">Загрузка…</p>
        </Card>
      ) : error ? (
        <Card header="Организация">
          <Alert variant="danger">{error}</Alert>
        </Card>
      ) : profile ? (
        <div>
          {profile.profileComplete === false ? (
            <Alert variant="info" className="mb-4">
              Профиль организации заполнен не полностью. Укажите обязательные реквизиты ниже.
            </Alert>
          ) : null}
          <CompanyProfileForm
            profile={profile}
            onSave={handleSave}
            hideManagerFields
            onDaDataLookup={companyApi.dadataLookup}
          />
        </div>
      ) : null}
      <ContactsManager api={companyApi} currentUserEmail={currentUserEmail} />
    </Grid>
  )
}

/**
 * Блок «Уведомления» в Настройках клиента (capability notifications, §3g): ИНФОРМАЦИОННЫЙ.
 * Клиент НЕ управляет видами/расписанием (это глобально настраивает администратор Центрики —
 * capability admin-notifications). Показываем перечень видов уведомлений (kind → ярлык) и
 * неактивный переключатель «Мессенджер «Макс»» (задел на уведомления через мессенджер; к каналу
 * Центрики в MAX из левого меню не относится) с пометкой «появится позже». Никакого
 * сохранения/управления.
 * У компании в перечне есть и письма о сроках экологии (ecology-reminders) — у физлица раздела
 * «Экология» нет.
 */
function NotificationsCard({ isCompany }: { isCompany: boolean }) {
  return (
    <Card header="Уведомления" className="mt-4">
      <Hint className="mb-4">
        Виды и расписание уведомлений настраивает администратор Центрики.
      </Hint>
      <ul>
        {[
          ...NOTIFICATION_INFO_KINDS,
          ...(isCompany ? NOTIFICATION_INFO_KINDS_COMPANY : []),
        ].map((kind) => (
          <li
            key={kind}
            className="flex items-center justify-between gap-4 border-b border-line py-3 last:border-b-0"
          >
            <span className="text-txt">{NOTIFICATION_KIND_LABELS[kind]}</span>
            <Badge variant="blue">Письмо</Badge>
          </li>
        ))}
      </ul>
      <div className="mt-4 border-t border-line pt-2">
        <SettingRow
          label="Мессенджер «Макс»"
          hint="появится позже"
          control={(id) => <Switch id={id} checked={false} disabled />}
        />
      </div>
    </Card>
  )
}

export function SettingsPage() {
  const { context } = useAuth()
  if (!context) return null
  const { user } = context
  const isCompany = user.role === 'company'
  const isClient = user.role === 'company' || user.role === 'individual'

  return (
    <div>
      <PageHead title="Настройки" />
      <Grid cols={2}>
        <Card header="Профиль">
          <Field label="ФИО">
            {(props) => <Input {...props} value={user.fullName} readOnly />}
          </Field>
          <Field label="Электронная почта">
            {(props) => <Input {...props} type="email" value={user.email} readOnly />}
          </Field>
          <Hint>Изменение почты — через менеджера Центрики.</Hint>
        </Card>
        <ChangePasswordCard />
      </Grid>

      {isCompany ? <CompanyOrgSection currentUserEmail={user.email} /> : null}

      {isCompany ? <EcoRequisites api={ecologyClientApi} withSites /> : null}

      {isClient ? <NotificationsCard isCompany={isCompany} /> : null}
    </div>
  )
}
